export type HreflangLink = {
  hreflang: string;
  href: string;
  rel?: string;
  hid?: string;
} & Record<string, unknown>;

/**
 * Normalizes a path/href to an absolute URL on the canonical host for the
 * given app bucket, rewriting promopromo hrefs to their myshopi alias.
 *
 * `baseUrl` must be resolved eagerly by the caller (via `useRequestURL()`)
 * outside of any lazily-evaluated computed/useHead callback -- Nuxt
 * composables that need request context can't be called from inside those,
 * since they may run outside the synchronous setup call stack during SSR.
 */
export function normalizeHref(
  href: string,
  host: string,
  baseUrl: { protocol: string; host: string },
): string {
  const shouldRewritePromoUrls = host === "promopromo";
  const normalizedBaseUrl = shouldRewritePromoUrls
    ? `${baseUrl.protocol}//${baseUrl.host.replace("promopromo", "myshopi")}`
    : `${baseUrl.protocol}//${baseUrl.host}`;

  return href.startsWith("http")
    ? shouldRewritePromoUrls
      ? href.replace("promopromo", "myshopi")
      : href
    : `${normalizedBaseUrl}${href}`;
}

/**
 * Computes hreflang metadata for the current page, scoped by host and locale.
 * Returns the allowed hreflangs, the effective current hreflang, and
 * filtered link sets for all valid and current hreflang entries.
 */
export default function useHrefLangs() {
  const head = useLocaleHead();
  const { locale } = useI18n();
  const host = useCurrentHost().value;
  const baseUrl = useRequestURL();
  const isPromoOrMyshopi = host === "promopromo" || host === "myshopi";

  const allowedHreflangs = computed<string[]>(() =>
    isPromoOrMyshopi ? ["x-default", "nl-BE", "fr"] : ["x-default", "nl-NL"],
  );

  const currentHreflang = computed<string>(() => {
    if (isPromoOrMyshopi && ["nl", "nl-NL"].includes(locale.value)) {
      return "nl-BE";
    }

    return locale.value;
  });

  const validHrefLangs = computed<HreflangLink[]>(() => {
    const normalizedLinks = (head.value.link as HreflangLink[])
      .filter((link) => allowedHreflangs.value.includes(link.hreflang))
      .map((link) => ({
        ...link,
        href: normalizeHref(link.href, host, baseUrl),
      }));

    const localizedHref = normalizedLinks.find(
      (link) => link.hreflang === currentHreflang.value,
    )?.href;

    // x-default should point to the same URL as the canonical.
    return normalizedLinks.map((link) =>
      link.hreflang === "x-default" && localizedHref
        ? { ...link, href: localizedHref }
        : link,
    );
  });

  const currentHrefLangs = computed<HreflangLink[]>(() =>
    validHrefLangs.value.filter(
      (link) => link.hreflang === currentHreflang.value,
    ),
  );

  return {
    allowedHreflangs,
    currentHreflang,
    validHrefLangs,
    currentHrefLangs,
  };
}
